import { useCallback } from 'react';

import {
  Currency,
  SupportedCurrency,
} from '@sorare/core/src/__generated__/globalTypes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { fromWei, toWei } from '@sorare/core/src/lib/wei';

type ReturnedProps = {
  formatPrice: (value: number, maxReachesEnd?: boolean) => string;
  sliderToPrice: (value: number) => string;
  priceToSlider: (value: number) => number;
};

export const useFormatFloorPricesFilters = (): ReturnedProps => {
  const { currency, fiatCurrency } = useCurrentUserContext();
  const {
    toMonetaryAmount,
    toMonetaryAmountInFiatCurrency,
    getUserFiatAmount,
  } = useMonetaryAmount();
  const { formatEth, formatNumber } = useIntlContext();

  const sliderToPrice = useCallback(
    (value: number): string => {
      const monetaryAmount = toMonetaryAmount({
        referenceCurrency: SupportedCurrency.EUR,
        eurCents: value,
      });

      if (currency === Currency.FIAT) {
        return (getUserFiatAmount(monetaryAmount) / 100).toFixed(2);
      }
      return fromWei(monetaryAmount.wei).toString();
    },
    [currency, toMonetaryAmount, getUserFiatAmount]
  );

  const formatPrice = useCallback(
    (value: number, maxReachesEnd?: boolean) => {
      const price = sliderToPrice(value);

      let formattedNumber;
      if (currency === Currency.FIAT) {
        formattedNumber = formatNumber(Number(price), {
          style: 'currency',
          currency: fiatCurrency.code,
        });
      } else {
        formattedNumber = formatEth(price);
      }

      return `${formattedNumber}${maxReachesEnd ? '+' : ''}`;
    },
    [currency, fiatCurrency.code, formatEth, formatNumber, sliderToPrice]
  );

  const priceToSlider = useCallback(
    (value: number): number => {
      if (currency === Currency.FIAT) {
        return toMonetaryAmountInFiatCurrency(value * 100).eurCents;
      }
      return toMonetaryAmount({
        wei: toWei(value),
        referenceCurrency: SupportedCurrency.WEI,
      }).eurCents;
    },
    [currency, toMonetaryAmountInFiatCurrency, toMonetaryAmount]
  );

  return {
    formatPrice,
    sliderToPrice,
    priceToSlider,
  };
};
